import { DateTime } from 'luxon';
import { HealthDirection } from 'models/enums/fleet-management';
import { HEALTH_STATUS } from 'models/fleet-management/FleetWidget';
import { FleetDataHealth, FleetDataHealthTransition } from 'models/fleet-management/FleetDataSet';
import { HEALTH_STATUS_ORDER } from './fleet-dashboard-theme';
import { rswVersionGroup } from './rsw-version';

export type RobotHealthStatus = Exclude<HEALTH_STATUS, HEALTH_STATUS.ALL>;

/** A transition named by its two colours, the key a band card selects the table with. */
export const transitionKey = (from: RobotHealthStatus | null, to: RobotHealthStatus) =>
  `${from ?? 'NONE'}>${to}`;

/** The directions that are a change of colour; unchanged and newly joined robots are not. */
export const CHANGE_DIRECTIONS = [
  HealthDirection.DETERIORATED,
  HealthDirection.RECOVERED,
  HealthDirection.WENT_DARK,
] as const;

export const isChange = (row: FleetDataHealthTransition) =>
  (CHANGE_DIRECTIONS as readonly HealthDirection[]).includes(row.direction);

export interface TransitionBand {
  direction: (typeof CHANGE_DIRECTIONS)[number];
  titleKey: string;
  noteKey: string;
  /** The status whose tone frames the band. */
  accent: RobotHealthStatus;
  /** Every colour pair the band can hold, most urgent first. */
  pairs: [RobotHealthStatus, RobotHealthStatus][];
}

const { GREEN, ORANGE, RED, GREY } = HEALTH_STATUS;

/**
 * Grouping by direction first means a reader never decodes a colour pair to learn whether a
 * number is good news: the band says so, the pair says how far.
 */
export const TRANSITION_BANDS: TransitionBand[] = [
  {
    direction: HealthDirection.DETERIORATED,
    titleKey: 'fleet-management-band-deteriorated',
    noteKey: 'fleet-management-band-deteriorated-note',
    accent: RED,
    pairs: [
      [GREEN, RED],
      [ORANGE, RED],
      [GREEN, ORANGE],
    ],
  },
  {
    direction: HealthDirection.RECOVERED,
    titleKey: 'fleet-management-band-recovered',
    noteKey: 'fleet-management-band-recovered-note',
    accent: GREEN,
    pairs: [
      [ORANGE, GREEN],
      [RED, ORANGE],
      [RED, GREEN],
      [GREY, GREEN],
      [GREY, ORANGE],
      [GREY, RED],
    ],
  },
  {
    direction: HealthDirection.WENT_DARK,
    titleKey: 'fleet-management-band-went-dark',
    noteKey: 'fleet-management-band-went-dark-note',
    accent: GREY,
    pairs: [
      [RED, GREY],
      [ORANGE, GREY],
      [GREEN, GREY],
    ],
  },
];

/** How many robots made each colour change, keyed by `transitionKey`. */
export const countTransitions = (rows: FleetDataHealthTransition[]) => {
  const counts = new Map<string, number>();
  rows.filter(isChange).forEach((row) => {
    const key = transitionKey(row.previous_status, row.new_status);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return counts;
};

/** Each band with only the pairs that happened, so an empty pair never takes a card. */
export const bandsWithCounts = (counts: Map<string, number>) =>
  TRANSITION_BANDS.map((band) => {
    const cards = band.pairs
      .map(([from, to]) => ({ from, to, key: transitionKey(from, to) }))
      .map((card) => ({ ...card, count: counts.get(card.key) ?? 0 }))
      .filter((card) => card.count > 0);
    return { ...band, cards, total: cards.reduce((sum, card) => sum + card.count, 0) };
  });

/**
 * How bad a change is, for sorting the worst to the top: a robot that fell two colours beats one
 * that fell one, falling beats going dark, and every recovery comes last.
 */
const SEVERITY: Record<string, number> = {
  [transitionKey(GREEN, RED)]: 6,
  [transitionKey(ORANGE, RED)]: 5,
  [transitionKey(GREEN, ORANGE)]: 4,
  [transitionKey(RED, GREY)]: 3,
  [transitionKey(ORANGE, GREY)]: 3,
  [transitionKey(GREEN, GREY)]: 3,
  [transitionKey(RED, ORANGE)]: 2,
};

export const transitionSeverity = (row: FleetDataHealthTransition) =>
  isChange(row) ? (SEVERITY[transitionKey(row.previous_status, row.new_status)] ?? 1) : 0;

export enum TransitionSort {
  RECENT = 'RECENT',
  SEVERITY = 'SEVERITY',
}

const changedAtMillis = (row: FleetDataHealthTransition) =>
  row.changed_at ? DateTime.fromISO(row.changed_at).toMillis() : -Infinity;

/** Changes first, in the chosen order; unchanged and joined robots after them by name. */
export const sortTransitions = (rows: FleetDataHealthTransition[], sort: TransitionSort) =>
  [...rows].sort((a, b) => {
    const changeOrder = Number(isChange(b)) - Number(isChange(a));
    if (changeOrder !== 0) return changeOrder;
    if (!isChange(a)) return a.name.localeCompare(b.name);

    const bySeverity = transitionSeverity(b) - transitionSeverity(a);
    const byRecency = changedAtMillis(b) - changedAtMillis(a);
    return sort === TransitionSort.SEVERITY ? bySeverity || byRecency : byRecency || bySeverity;
  });

/**
 * The day a still-green robot counts as at risk: four days before it would turn orange on the
 * standard 14-day limit, scaled for the tighter adoption limits so a 3-day robot is flagged
 * one day early rather than from the moment its mission ends.
 */
export const atRiskFromDays = (greenLimitDays: number) =>
  greenLimitDays - Math.max(1, Math.round((greenLimitDays * 4) / 14));

export const isAtRisk = (row: FleetDataHealthTransition) =>
  row.new_status === GREEN &&
  row.days_since_success != null &&
  row.green_limit_days > 0 &&
  row.days_since_success >= atRiskFromDays(row.green_limit_days);

/** The at-risk robots, the closest to their limit first. */
export const atRiskRobots = (rows: FleetDataHealthTransition[]) =>
  rows
    .filter(isAtRisk)
    .sort(
      (a, b) =>
        (b.days_since_success ?? 0) / b.green_limit_days -
          (a.days_since_success ?? 0) / a.green_limit_days || a.name.localeCompare(b.name),
    );

export interface VersionHealthRow {
  version: string;
  total: number;
  counts: Record<RobotHealthStatus, number>;
}

const emptyCounts = (): Record<RobotHealthStatus, number> => ({
  GREEN: 0,
  ORANGE: 0,
  RED: 0,
  GREY: 0,
});

/**
 * Each RSW release's robots (see `rswVersionGroup`) split by the colour they ended the window in. The biggest `limit`
 * versions keep a row of their own and the rest share one `otherLabel` row, the way
 * `topNWithOther` folds a category list.
 */
export const versionHealthCrossTab = (
  rows: FleetDataHealthTransition[],
  limit: number,
  otherLabel: string,
  unknownLabel: string,
): VersionHealthRow[] => {
  const byVersion = new Map<string, VersionHealthRow>();
  rows.forEach((row) => {
    const rsw = row.software_version__rsw_software_version;
    const version = rsw ? rswVersionGroup(rsw) : unknownLabel;
    const entry = byVersion.get(version) ?? { version, total: 0, counts: emptyCounts() };
    entry.total += 1;
    entry.counts[row.new_status] += 1;
    byVersion.set(version, entry);
  });

  const sorted = [...byVersion.values()].sort(
    (a, b) => b.total - a.total || b.version.localeCompare(a.version),
  );
  if (sorted.length <= limit) return sorted;

  const other = sorted.slice(limit).reduce<VersionHealthRow>(
    (sum, entry) => {
      HEALTH_STATUS_ORDER.forEach((status) => {
        sum.counts[status] += entry.counts[status];
      });
      return { ...sum, total: sum.total + entry.total };
    },
    { version: otherLabel, total: 0, counts: emptyCounts() },
  );
  return [...sorted.slice(0, limit), other];
};

export interface MomentumWeek {
  weekStart: string;
  improved: number;
  /** Robots that lost colour, including those that went dark. */
  declined: number;
  net: number;
  unchanged: number;
  active: number;
}

/** The newest two weeks of the health report, newest first. */
export const momentumWeeks = (healthData: FleetDataHealth[]): MomentumWeek[] =>
  [...healthData]
    .sort(
      (a, b) =>
        DateTime.fromISO(b.week_start).toMillis() - DateTime.fromISO(a.week_start).toMillis(),
    )
    .slice(0, 2)
    .map((week) => {
      const improved = week.improved_count ?? 0;
      const declined = (week.deteriorated_count ?? 0) + (week.went_dark_count ?? 0);
      return {
        weekStart: week.week_start,
        improved,
        declined,
        net: improved - declined,
        unchanged: week.unchanged_count ?? 0,
        active: week.active_robot_count ?? 0,
      };
    });

/** Whole days from `from` to `to`, never negative. */
export const daysBetween = (from: string, to: DateTime) =>
  Math.max(0, Math.floor(to.diff(DateTime.fromISO(from), 'days').days));
